/**
 * The family code, shown once on the way into the valley.
 *
 * Once, and then never again from here -- because it does not have to be.
 * Settings shows it to any member on demand, which is the difference between
 * this and the "write this down, we can never show it again" phrase it
 * replaced. There is deliberately no "I have written it down" checkbox: the
 * checkbox existed because losing the phrase was unrecoverable, and losing
 * the code is not.
 *
 * So this is an introduction rather than a hurdle. It says what the code is
 * for, offers the two things a parent would want to do with it right now, and
 * has one button that means "yes, I have seen it".
 */

import { Secret } from '../components/Secret';
import { formatFamilyCode, joinLink } from '../family/families';
import { ShareActions } from './ShareActions';

export function FamilyCodeBanner({
  code,
  familyName,
  onDone,
}: {
  code: string;
  familyName: string;
  onDone: () => void;
}) {
  return (
    <>
      <h2>
        {familyName ? `${familyName} has a family code` : 'Your family has a family code'}
      </h2>
      <p>
        One code for the whole family. Send it to another parent and they can
        join; type it on a new device and it unlocks your family&rsquo;s names
        and characters there.
      </p>

      <Secret value={formatFamilyCode(code)} label="Your family code" />

      <ShareActions code={code} familyName={familyName} link={joinLink(code)} />

      <p className="footnote">
        <strong>You do not need to write this down.</strong> Find it any time
        in Settings &rarr; Family code, on any device that is already unlocked.
        You can change it there too, which stops any link you have already sent
        from working. What we store is your family&rsquo;s key locked with this
        code, and a hash of it we cannot reverse &mdash; so we cannot read it
        and cannot show it to anyone.
      </p>

      <button className="primary full" onClick={onDone}>
        Take me to the valley
      </button>
    </>
  );
}
